import { Autocomplete, Stack, TextField, Typography, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PrintIcon from '@mui/icons-material/Print';
import { DarkUpperSection, LoadingSpinner, PageSection, StyledButton } from '../../components';
import { useAlert, useCurrentUser, useDatastore } from '../../hooks';
import { useEffect, useMemo, useState } from 'react';
import { UserDetailsProps } from '../../datastore/userDetails';
import { LocationProps, UnknownLocation, LocationType } from '../../datastore/locations';
import { PrinterProps } from '../../datastore/printers';
import { useNavigate } from 'react-router-dom';
import { useDirtyContext } from '../../hooks/useDirtyProvider';
import { ConfirmationDialog } from '../../components/modals/ConfirmDialog';

export const SUPER_ADMINS = [
  'Gavin.Dillon@mrl.com.au',
  'Matt.Tye@mrl.com.au',
  'Jeff.Scherpenzeel@mrl.com.au',
  'Krissa.DeTorres@mrl.com.au'
];
export const UAT_SUPER_ADMINS = [...SUPER_ADMINS, ...['Billy.Li@mrl.com.au', 'liheng.zhang@mrl.com.au']];

export default function Settings() {
  const theme = useTheme();
  const user = useCurrentUser();
  const navigate = useNavigate();
  const { isDirty, setIsDirty } = useDirtyContext();
  const [details, setDetails] = useState<UserDetailsProps>({} as UserDetailsProps);
  const [locations, setLocations] = useState<LocationProps[]>([]);
  const [printers, setPrinters] = useState<PrinterProps[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [navigateDestination, setNavigateDestination] = useState('');
  const [originalLocationName, setOriginalLocationName] = useState<string>('');
  const [originalPrinter, setOriginalPrinter] = useState<PrinterProps | null>();
  const [openDirtyDialog, setOpenDirtyDialog] = useState(false);
  const store = useDatastore();
  const alert = useAlert();

  const adminPanelAccessSettingsAccess = useMemo(() => {
    const superAdminList = process.env.REACT_APP_IS_PROD === 'true' ? SUPER_ADMINS : UAT_SUPER_ADMINS;

    return superAdminList.map((sa) => sa.toLowerCase()).includes(user.email.toLowerCase());
  }, [user.email]);

  useEffect(() => {
    async function getData() {
      const results = await store.userDetails.get(user.cognitoUser.username, user.cognitoUser);
      if (results) {
        setDetails(results);
        setOriginalLocationName(results.location?.name || '');
        setOriginalPrinter(results.printer ?? null);
        setIsLoading(false);
      }
    }

    async function getLocationData() {
      const results = await store.locations.getList();
      const mainLocations = results?.filter((l) => l.type === LocationType.mainLocation && !l.isDisabled);
      if (mainLocations) {
        setLocations(mainLocations);
      }
    }

    getData();
    getLocationData();
  }, [store.locations, store.userDetails, user.cognitoUser]);

  useEffect(() => {
    async function getPrinterData() {
      const results = await store.printers.getAll();
      const enabledPrinters = results?.filter((p) => !p.isDisabled);
      if (enabledPrinters) {
        setPrinters(enabledPrinters);
      }
    }
    getPrinterData();
  }, [details.printer, store.printers]);

  function updateLocation(name: string) {
    const location = locations.find((l) => l.name === name);
    const isOriginalLocation = location?.name === originalLocationName;
    setDetails((prevDetails) => ({
      ...prevDetails,
      location,
      printer: originalPrinter
    }));

    setIsDirty(!isOriginalLocation);
  }

  async function updatePrinter(value: string | null) {
    if (value) {
      const newPrinter = await store.printers.getById(value);
      setDetails((prevState) => ({
        ...prevState,
        printer: newPrinter
      }));

      setIsDirty(newPrinter?.id !== originalPrinter?.id);
    }
  }

  async function handleSaveDetails() {
    if (details?.location?.id === undefined) {
      alert('Please select a location', 'warning');
      return;
    }
    const results = await store.userDetails.update(details);
    updateDetails(results);
  }

  function updateDetails(results: UserDetailsProps) {
    if (results) {
      setDetails(results);
      setIsDirty(false);
      setOriginalLocationName(results.location?.name || '');
      setOriginalPrinter(results.printer);
      alert('Settings updated.', 'success');
    } else {
      alert('Could not update settings', 'error');
    }
  }

  function handleNavigateAdmin(destination: string) {
    setNavigateDestination(destination);
    if (isDirty) {
      setOpenDirtyDialog(true);
    } else {
      navigate(`/settings/${destination}`);
    }
  }

  const orderedLocations = [
    { value: '', name: 'Choose Location', id: crypto.randomUUID() },
    ...locations.sort((a, b) => a.name.localeCompare(b.name))
  ].filter((location) => location.name !== UnknownLocation.Name);

  const orderedPrinters = printers.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      <DarkUpperSection>
        <Stack spacing={2} sx={{ padding: '1em 0' }}>
          <Typography variant="h2" fontWeight="bold" color={theme.palette.primary.contrastText}>
            Settings
          </Typography>
          <Typography variant="body1" color={theme.palette.primary.contrastText}>
            Configure your location and printer preferences
          </Typography>
        </Stack>
      </DarkUpperSection>
      {user.isAdmin && (
        <PageSection>
          <Stack spacing={3} sx={{ padding: '1.5em 0', maxWidth: '400px', margin: '0 auto' }}>
            <Box
              sx={{
                backgroundColor: 'background.default',
                borderRadius: 2,
                padding: 2.5,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
              <Stack spacing={2.5}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <SettingsIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
                  <Stack spacing={0}>
                    <Typography variant="h6" fontWeight={600}>
                      Admin Settings
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manage FTL settings
                    </Typography>
                  </Stack>
                </Stack>
                <LoadingSpinner isLoading={isLoading}>
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <StyledButton
                      variant="outlined"
                      startIcon={<LocationOnIcon />}
                      sx={{
                        whiteSpace: 'nowrap',
                        paddingY: 1.5,
                        borderRadius: 2,
                        flex: 1
                      }}
                      onClick={() => handleNavigateAdmin('locations')}>
                      Location Settings
                    </StyledButton>
                    <StyledButton
                      variant="outlined"
                      startIcon={<PrintIcon />}
                      sx={{
                        whiteSpace: 'nowrap',
                        paddingY: 1.5,
                        borderRadius: 2,
                        flex: 1
                      }}
                      onClick={() => handleNavigateAdmin('printers')}>
                      Printer Settings
                    </StyledButton>
                  </Stack>
                  {adminPanelAccessSettingsAccess ? (
                    <Stack direction="row" spacing={2} justifyContent="center">
                      <StyledButton
                        variant="outlined"
                        startIcon={<AdminPanelSettingsIcon />}
                        sx={{
                          whiteSpace: 'nowrap',
                          paddingY: 1.5,
                          borderRadius: 2,
                          flex: 1
                        }}
                        onClick={() => handleNavigateAdmin('adminPanelAccessSettings')}>
                        Admin Panel Access Settings
                      </StyledButton>
                    </Stack>
                  ) : (
                    <></>
                  )}
                </LoadingSpinner>
              </Stack>
            </Box>
          </Stack>
        </PageSection>
      )}
      <PageSection>
        <LoadingSpinner isLoading={isLoading}>
          <Stack spacing={4} sx={{ padding: '1.5em 0', maxWidth: '400px', margin: '0 auto' }}>
            {/* Location Setting */}
            <Stack spacing={2}>
              <Stack spacing={0.5}>
                <Typography variant="h4" fontWeight="bold">
                  Location
                </Typography>
                <Typography color={theme.palette.text.secondary}>Select your current working location</Typography>
              </Stack>
              <Autocomplete
                value={orderedLocations.find((loc) => loc.name === details?.location?.name) || null}
                onChange={(event, newValue) => {
                  updateLocation(newValue?.name || '');
                }}
                options={orderedLocations}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.name === value.name}
                renderInput={(params) => <TextField {...params} placeholder="Search locations..." variant="outlined" />}
                sx={{ minWidth: '200px' }}
              />
            </Stack>

            {/* Printer Setting */}
            <Stack spacing={2}>
              <Stack spacing={0.5}>
                <Typography variant="h4" fontWeight="bold">
                  Printer
                </Typography>
                <Typography color={theme.palette.text.secondary}>
                  Choose your default printer for label printing
                </Typography>
              </Stack>
              <Autocomplete
                value={orderedPrinters.find((printer) => printer.id === details?.printer?.id) || null}
                onChange={(event, newValue) => {
                  updatePrinter(newValue?.id || null);
                }}
                options={orderedPrinters}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={!details?.location?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={!details?.location?.id ? 'Select a location first' : 'Search printers...'}
                    variant="outlined"
                  />
                )}
                sx={{ minWidth: '200px' }}
              />
              {!details?.location?.id && (
                <Typography variant="body2" color={theme.palette.warning.main}>
                  Please select a location before choosing a printer
                </Typography>
              )}
            </Stack>

            {/* Save Changes Button */}
            <Stack direction="row" justifyContent="center" sx={{ pt: 1 }}>
              <StyledButton
                variant="contained"
                onClick={handleSaveDetails}
                disabled={!isDirty}
                sx={{ whiteSpace: 'nowrap', px: 6, py: 1.5 }}>
                Save Changes
              </StyledButton>
            </Stack>
          </Stack>
        </LoadingSpinner>
      </PageSection>

      <ConfirmationDialog
        open={openDirtyDialog}
        onClose={() => setOpenDirtyDialog(false)}
        onConfirm={() => navigate(`/settings/${navigateDestination}`)}
        title="Unsaved Changes"
        content="You have unsaved changes. Are you sure you want to leave the page?"
      />
    </>
  );
}
